/**
 * CosmeticCard — én ting i Butikk-katalogen (#156, steg 6b; Stell_Butikk).
 *
 * Glasskort, radius 24, som belønningskortet på Premier: forhåndsvisningen av
 * tingen på avataren, navnet (15/800), og nederst en statustekst og
 * handlingen. En kosmetisk ting eies for alltid når den er kjøpt:
 *
 *   ikke eid + har råd + gammel nok → pris + «Kjøp»          → onBuy
 *   ikke eid + for ung              → pris + «Fra N år»      (ikke trykkbar)
 *   ikke eid + for dyr              → pris + «Trenger N p»   (ikke trykkbar)
 *   eid + i bruk                    → «I bruk» + «Ta av»     → onEquip(false)
 *   eid + ikke i bruk               → «Kjøpt» + «Bruk»       → onEquip(true)
 *
 * Før saldoen er hentet står bare prisen, i stedet for å blinke som «for dyr».
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives/Button';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { formatPoints } from '../../i18n/formatters';
import { CosmeticAvatar } from './CosmeticAvatar';
import type { CosmeticCatalogRow } from './useCosmetics';

interface CosmeticCardProps {
  item: CosmeticCatalogRow;
  /** Saldoen til den innloggede. `undefined` før den er hentet. */
  balance: number | undefined;
  colorSlot: ColorSlot;
  busy?: boolean;
  onBuy: () => void;
  onEquip: (equipped: boolean) => void;
}

/** Låst og «trenger mer»: samme form som knappen, men dempet og ikke trykkbar. */
const BLOCKED =
  'inline-flex h-11 items-center rounded-pill bg-track px-3.5 text-control font-extrabold text-ink-2';

export function CosmeticCard({
  item,
  balance,
  colorSlot,
  busy = false,
  onBuy,
  onEquip,
}: CosmeticCardProps): ReactNode {
  const { t } = useTranslation();
  const known = balance !== undefined;
  const need = known ? Math.max(0, item.point_cost - balance) : 0;
  const price = t('rewards.points', { points: formatPoints(item.point_cost) });

  let status: ReactNode = <span className="tnum">{price}</span>;
  let action: ReactNode = null;
  if (item.owned && item.equipped) {
    status = <span data-testid="cosmetic-equipped-badge">{t('shop.action.inUse')}</span>;
    action = (
      <Button
        variant="secondary"
        size={44}
        data-testid="cosmetic-unequip"
        disabled={busy}
        onClick={() => onEquip(false)}
      >
        {t('shop.action.unequip')}
      </Button>
    );
  } else if (item.owned) {
    status = <span>{t('shop.action.owned')}</span>;
    action = (
      <Button
        variant="solid"
        size={44}
        data-testid="cosmetic-equip"
        disabled={busy}
        onClick={() => onEquip(true)}
      >
        {t('shop.action.equip')}
      </Button>
    );
  } else if (item.locked_age) {
    action = (
      <span data-testid="cosmetic-age-locked" className={BLOCKED}>
        {t('shop.action.ageLocked', { count: item.min_age })}
      </span>
    );
  } else if (known && need === 0) {
    action = (
      <Button size={44} data-testid="cosmetic-buy" disabled={busy} onClick={onBuy}>
        {t('shop.action.buy')}
      </Button>
    );
  } else if (known) {
    action = (
      <span data-testid="cosmetic-need-more" className={`tnum ${BLOCKED}`}>
        {t('shop.action.needMore', { count: formatPoints(need) })}
      </span>
    );
  }

  return (
    <article
      data-testid="cosmetic-card"
      data-kind={item.kind}
      data-owned={item.owned ? 'true' : 'false'}
      className={`${GLASS_SURFACE} flex flex-col items-start gap-2 rounded-lg p-3.5 shadow-md`}
    >
      <CosmeticAvatar
        colorSlot={colorSlot}
        equipped={[{ kind: item.kind, asset_key: item.asset_key }]}
        size={56}
      />
      <h2 className="m-0 text-body leading-tight font-extrabold text-ink">{item.name}</h2>
      {item.description ? (
        <p className="m-0 line-clamp-2 text-xs text-ink-2">{item.description}</p>
      ) : null}
      <div className="mt-auto flex w-full flex-wrap items-center justify-between gap-2">
        <span className="text-control font-extrabold text-ink-2">{status}</span>
        {action}
      </div>
    </article>
  );
}
